import React, { useState } from 'react';
import { billingService } from '../clients/ApiClients';
import { Button } from '../common/Catalyst/button';
import { Input } from '../common/Catalyst/input';
import { Switch } from '../common/Catalyst/switch';
import { Subheading } from '../common/Catalyst/heading';
import { Text } from '../common/Catalyst/text';
import ShowAlert from '../ShowAlert';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import {
  billingErrorMessage,
  formatBillingDate,
  formatMoney,
} from '../utils/billingHelpers';

interface Props {
  hoaId: string;
}

type JobKey = 'late_fees' | 'interest' | 'aging' | 'notices' | 'verify';

interface Report {
  key: JobKey;
  lines: string[];
  skipped?: string | null;
  drift?: { home: string; cached: string; computed: string }[];
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The nightly jobs, run by hand.
 *
 * Each one is idempotent — a second run over the same period writes
 * nothing — so re-running is safe, and the report says what it did
 * rather than what it would have done.
 *
 * **`skipped_reason` is shown verbatim.** "This HOA has no late fee
 * configured" is a different answer from "nothing was owed", and
 * flattening both to a zero is how somebody concludes the job is broken.
 *
 * **The drift check never repairs by default.** A cached balance that
 * disagrees with the ledger is a bug worth understanding, and repairing
 * it destroys the evidence of whatever wrote it.
 */
const BillingJobsPanel: React.FC<Props> = ({ hoaId }) => {
  const [asOf, setAsOf] = useState('');
  const [repair, setRepair] = useState(false);
  const [running, setRunning] = useState<JobKey | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dateError, setDateError] = useState('');

  const run = async (key: JobKey): Promise<void> => {
    if (asOf && !DATE.test(asOf)) {
      setDateError('Use a date, or leave blank for today.');
      return;
    }
    setDateError('');
    try {
      setRunning(key);
      setError(null);
      setReport(null);
      const body = { as_of: asOf || null };

      if (key === 'late_fees') {
        const { data } = await billingService.runLateFees({
          hoaId,
          runAsOfRequest: body,
        });
        setReport({
          key,
          skipped: data.skipped_reason,
          lines: [
            `${data.entries_written} late ${data.entries_written === 1 ? 'fee' : 'fees'} written across ${data.homes_charged} ${data.homes_charged === 1 ? 'home' : 'homes'}`,
            `${formatMoney(data.total_amount)} charged in total`,
            `as of ${formatBillingDate(data.as_of)}`,
          ],
        });
      } else if (key === 'interest') {
        const { data } = await billingService.runInterest({
          hoaId,
          runAsOfRequest: body,
        });
        setReport({
          key,
          skipped: data.skipped_reason,
          lines: [
            `${data.entries_written} interest ${data.entries_written === 1 ? 'accrual' : 'accruals'} across ${data.homes_charged} ${data.homes_charged === 1 ? 'home' : 'homes'}`,
            `${formatMoney(data.total_amount)} charged in total`,
            `as of ${formatBillingDate(data.as_of)}`,
          ],
        });
      } else if (key === 'aging') {
        const { data } = await billingService.runAging({
          hoaId,
          runAsOfRequest: body,
        });
        setReport({
          key,
          lines: [
            `${data.homes_checked} homes checked`,
            `${data.cases_opened} opened, ${data.cases_advanced} advanced, ${data.cases_resolved} resolved`,
            `as of ${formatBillingDate(data.as_of)}`,
          ],
        });
      } else if (key === 'notices') {
        const { data } = await billingService.runNotices({
          hoaId,
          runAsOfRequest: body,
        });
        setReport({
          key,
          lines: [
            `${data.rules_evaluated} ${data.rules_evaluated === 1 ? 'rule' : 'rules'} evaluated`,
            `${data.notices_sent} sent, ${data.notices_flagged} flagged for a person to release`,
            `as of ${formatBillingDate(data.as_of)}`,
          ],
        });
      } else {
        const { data } = await billingService.verifyBalances({
          hoaId,
          repair,
        });
        setReport({
          key,
          lines: [
            `${data.homes_checked} homes checked`,
            data.drift_count === 0
              ? 'Every cached balance agrees with the ledger.'
              : `${data.drift_count} ${data.drift_count === 1 ? 'balance disagrees' : 'balances disagree'} with the ledger.`,
            data.repaired
              ? 'Repaired — the evidence of what wrote them is gone.'
              : 'Not repaired, deliberately.',
          ],
          drift: data.drifted.map((d) => ({
            home: d.home_id,
            cached: d.cached_balance,
            computed: d.computed_balance,
          })),
        });
      }
    } catch (err) {
      console.error('Error running job:', err);
      setError(billingErrorMessage(err));
    } finally {
      setRunning(null);
    }
  };

  const jobs: { key: JobKey; label: string; help: string }[] = [
    {
      key: 'late_fees',
      label: 'Late fees',
      help: 'Charges the association’s late fee against anything past its grace period.',
    },
    {
      key: 'interest',
      label: 'Interest',
      help: 'Accrues interest on overdue balances, rounded once at the entry.',
    },
    {
      key: 'aging',
      label: 'Aging & collections',
      help: 'Recomputes days past due, opens cases, and takes them as far as a late notice.',
    },
    {
      key: 'notices',
      label: 'Notices',
      help: 'Evaluates the notice schedule — sends what is set to send, flags the rest.',
    },
    {
      key: 'verify',
      label: 'Check balances',
      help: 'Recomputes every home from its ledger and reports anything the cache disagrees with.',
    },
  ];

  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-6">
      <Subheading>Billing jobs</Subheading>
      <Text className="mt-1">
        These normally run on a schedule. Running one by hand is safe — each is
        idempotent, so a second run over the same period writes nothing.
      </Text>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
            As of
          </label>
          <Input
            type="date"
            value={asOf}
            onChange={(e) => {
              setAsOf(e.target.value);
              setDateError('');
            }}
            className="w-40"
          />
          <ErrorAlert className="mt-1">{dateError}</ErrorAlert>
        </div>
        <Text className="text-xs">
          Blank means today, on the association&rsquo;s calendar.
        </Text>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {jobs.map((j) => (
          <div
            key={j.key}
            data-testid={`job-${j.key}`}
            className="rounded-md border border-zinc-200 dark:border-zinc-700 p-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium text-zinc-900 dark:text-white">
                  {j.label}
                </div>
                <Text className="mt-1 text-xs">{j.help}</Text>
              </div>
              <Button
                outline
                onClick={() => run(j.key)}
                disabled={running !== null}
              >
                {running === j.key ? 'Running…' : 'Run'}
              </Button>
            </div>
            {j.key === 'verify' && (
              <div className="mt-3 flex items-center gap-2">
                <Switch
                  checked={repair}
                  onChange={setRepair}
                  aria-label="Repair drifted balances"
                />
                <Text className="text-xs">
                  Also repair what it finds — <strong>rarely right</strong>.
                  Drift is a bug to understand, and repairing it destroys the
                  evidence.
                </Text>
              </div>
            )}
          </div>
        ))}
      </div>

      {error && (
        <div className="mt-4">
          <ShowAlert
            alertType="error"
            alertTitle="The job did not run"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={14000}
            isInline
          />
        </div>
      )}

      {report && (
        <div className="mt-4 rounded-md border border-zinc-200 dark:border-zinc-700 p-4">
          <Subheading level={4}>
            {jobs.find((j) => j.key === report.key)?.label}
          </Subheading>
          {report.skipped ? (
            <Text className="mt-2">
              <strong>Skipped:</strong> {report.skipped}
            </Text>
          ) : (
            <ul className="mt-2 space-y-1">
              {report.lines.map((l) => (
                <li key={l} className="text-sm">
                  {l}
                </li>
              ))}
            </ul>
          )}
          {report.drift && report.drift.length > 0 && (
            <ul className="mt-3 space-y-1">
              {report.drift.map((d) => (
                <li key={d.home} className="text-sm">
                  {d.home}: cached {formatMoney(d.cached)}, ledger says{' '}
                  {formatMoney(d.computed)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default BillingJobsPanel;
